import { useTheme } from '@material-ui/core'
import { AutocompleteChangeReason } from '@material-ui/lab'
import {
  KeywordWithTranslation,
  useKeywordsTranslation,
} from 'components/Common/ArticleSelector/useKeywordsTranslation'
import { MaterialsController } from 'controllers/MaterialsController'
import { useController } from 'customHooks/useController'
import { debounce, groupBy, isEqual } from 'lodash'
import { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from 'store/configureStore'
import { useAppDispatch } from 'store/configureStore'
import {
  KeywordDto,
  MaterialSummaryDto
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { ShowException } from 'store/Application/appActions'
import { MaterialActions } from 'store/MaterialSelector/MaterialActions'
import { Key } from 'utils/keyCodes'
import { useOrganizationAndProjectContext } from 'utils/useOrganizationContext'
import { fetchProject } from '../asyncActions/fetchProject'
import { saveBomItemMaterials } from '../asyncActions/saveBomItemMaterials'
import { newProjectActions } from '../newProjectReducer'
import { bomItemIsPurchasingPartSelector } from '../selectors/bomItemIsPurchasingPartSelector'
import { bomItemMaterialSummarySelector } from '../selectors/bomItemMaterialSummarySelector'
import { projectOperationSelector } from '../selectors/projectOperationPendingSelector'
import { bomItemWorkingStepsSelector } from '../selectors/bomItemWorkingStepsSelector'

export const useMaterialKeywords = (bomItemId?: string) => {
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const { t, i18n } = useTranslation()
  const { translateKeyword, asArrayOfTranslatedKeywords } =
    useKeywordsTranslation()

  const initialMaterialSummary = useAppSelector(
    bomItemMaterialSummarySelector(bomItemId),
    isEqual
  )

  const workingSteps = useAppSelector(
    bomItemWorkingStepsSelector({ bomItemId }),
    isEqual
  )

  const [materialSummary, setMaterialSummary] = useState(initialMaterialSummary)

  // const isEditing = useRef(false)
  const [isEditing, setIsEditing] = useState(false)

  useEffect(() => {
    setMaterialSummary(initialMaterialSummary)
  }, [initialMaterialSummary])

  const resetMaterialSummary = () => {
    setIsEditing(false)
    setMaterialSummary(initialMaterialSummary)
  }

  const { partyId, projectId } = useOrganizationAndProjectContext()
  const { controller, loading } = useController(
    () => new MaterialsController(partyId)
  )
  const isPurchasingPart = useAppSelector(
    bomItemIsPurchasingPartSelector(bomItemId)
  )

  const saving = useAppSelector(
    projectOperationSelector(
      saveBomItemMaterials.typePrefix.concat('/', bomItemId)
    ),
    isEqual
  )

  useEffect(() => {
    if (saving?.requestStatus === 'fulfilled') {
      handleClose()
    }
  }, [saving])

  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState<KeywordWithTranslation[]>()

  const getNewKeywords = useCallback(
    (selectedKeywords) => {
      return debounce(
        async () => {
          try {
            setOptions([])
            // setIsEditing(true)
            const workingStepList = workingSteps
              ? workingSteps.map(w => w.primaryWorkingStep)
              : []

            const keywords = await controller.GetMaterialsAsync(
              selectedKeywords,
              null,
              projectId,
              isPurchasingPart,
              workingStepList
            )

            // setOptions(current => ({ ...current, asArrayOfTranslatedKeywords(keywords.selectableTokens)}))
            setOptions((current) => {
              if (current) {
                return [
                  ...(keywords.selectedTokens as KeywordWithTranslation[]),
                  ...asArrayOfTranslatedKeywords(keywords.selectableTokens),
                ].sort((a, b) =>
                  a.category.localeCompare(b.category, i18n.language)
                )
              } else {
                return asArrayOfTranslatedKeywords(
                  keywords.selectableTokens
                ).sort((a, b) =>
                  a.category.localeCompare(b.category, i18n.language)
                )
              }
            })

            return keywords
          } catch (err) {
            ShowException('project', err)
            return []
          }
        },
        120,
        { leading: false, trailing: true }
      )
    },
    [
      asArrayOfTranslatedKeywords,
      controller,
      isPurchasingPart,
      projectId,
      i18n.language,
      workingSteps
    ]
  )

  useEffect(() => {
    if (open) {
      getNewKeywords(
        Object.values(materialSummary.tokens).flatMap((x) =>
          x.map((x) => x.originalKeyword)
        )
      )()
    }
  }, [open, getNewKeywords, materialSummary?.tokens])

  const handleSaveKeywords = async (material: MaterialSummaryDto) => {
    try {
      if (isEqual(materialSummary.tokens, initialMaterialSummary.tokens)) return
      if (Object.keys(material.tokens || {}).length === 0) return // no keywords selected

      //when changing just keywords, should not send an already defined articleId
      const newMaterial = { ...material, id: undefined }

      dispatch(saveBomItemMaterials({ bomItemId, material: newMaterial }))
      setIsEditing(false)
      dispatch(newProjectActions.setBomItemInSearch(bomItemId))
    } catch (err) {
      ShowException('project', err)
      dispatch(fetchProject({ projectId: projectId }))
    }
  }

  const handleChange = (
    e: ChangeEvent<unknown>,
    value: KeywordDto[],
    reason: AutocompleteChangeReason
  ) => {
    switch (reason) {
      case 'select-option':
      case 'remove-option':
        {
          if (value) {
            setMaterialSummary((current) => ({
              ...current,
              tokens: groupBy(value, (x) => x.category),
            }))
            getNewKeywords(value.map((x) => x.originalKeyword))
            setOpen(true)
            setIsEditing(true)
          }
        }
        break
      case 'blur':
        {
          if (Object.keys(materialSummary.tokens || {}).length === 0) {
            resetMaterialSummary()
          } else {
            handleSaveKeywords(materialSummary)
          }
        }
        break
      case 'clear': {
        setMaterialSummary((current) => ({
          ...current,
          tokens: {},
        }))
      }
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === Key.Enter && options.length === 0) {
      handleSaveKeywords(materialSummary)
      setOpen(false)
    }
    if (e.key === Key.Escape) {
      resetMaterialSummary()
    }
    if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
      dispatch(MaterialActions.copyMaterial(materialSummary))
    }
    if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
      dispatch(MaterialActions.pasteMaterial([bomItemId]))
      setIsEditing(true)
    }

    e.stopPropagation()
  }

  const handleClose = () => {
    setOpen(false)
    setOptions([])
    setIsEditing(false)
  }

  return {
    // initialMaterialSummary,
    options,
    loading,
    open,
    setOpen,
    getNewKeywords,
    handleSaveKeywords,
    saving,
    t,
    translateKeyword,
    theme,
    materialSummary,
    setMaterialSummary,
    resetMaterialSummary,
    handleKeyDown,
    handleClose,
    handleChange,
    isEditing,
  }
}
